import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  NightsStay as NightsStayIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CompareArrows as CompareArrowsIcon,
  Psychology as InsightAiIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { Quote } from "./hooks/useCompanyQuote";
import { formatPercentage } from "./utils/formatNumber";
import { useCompanyProfile } from "./hooks/useCompanyProfile";
import {
  useCompanyAftermarketTrade,
  AftermarketTrade,
} from "./hooks/useCompanyAftermarketTrade";
import { useExchangeMarketHours } from "./hooks/useExchangeMarketHours";
import { useIsStockInWatchlist } from "../Watchlists/hooks/useIsStockInWatchlist";
import { useSelectedWatchlist } from "../Watchlists/hooks/useSelectedWatchlist";
import WatchlistMembershipPicker from "../Watchlists/WatchlistMembershipPicker";
import WatchlistNameModal from "../Watchlists/WatchlistNameModal";
import { hapticError, hapticSelection } from "../../../lib/haptics";
import { useAddStockToWatchlist } from "../Watchlists/hooks/useAddStockToWatchlist";
import { useRemoveStockFromWatchlist } from "../Watchlists/hooks/useRemoveStockFromWatchlist";
import StockPriceChart from "./components/StockPriceChart";
import SearchBar from "../../common/SearchBar/SearchBar";
import Logo from "../../shared/Logo";
import { getSymbolImageUrl } from "../../../lib/symbolImage";
import { useMembership } from "../../../contexts/MembershipContext";
import RecentDevelopmentsCard from "./components/RecentDevelopmentsCard";
import RevenueChart from "./components/RevenueChart";
import RevenueProjectionsChart from "./components/RevenueProjectionsChart";
import NetIncomeChart from "./components/NetIncomeChart";
import CashFlowBreakdownChart from "./components/CashFlowBreakdownChart";
import EPSChart from "./components/EPSChart";
import MarketCapChart from "./components/MarketCapChart";
import VolumeChart from "./components/VolumeChart";
import SharesOutstandingChart from "./components/SharesOutstandingChart";
import CashDebtBreakdownChart from "./components/CashDebtBreakdownChart";
import EbitdaChart from "./components/EbitdaChart";
import ExpensesChart from "./components/ExpensesChart";
import MarginsChart from "./components/MarginsChart";
import AltmanZScoreChart from "./components/AltmanZScoreChart";
import PiotroskiScoreChart from "./components/PiotroskiScoreChart";
import EmployeeCountChart from "./components/EmployeeCountChart";
import RevenueSegmentationChart from "./components/RevenueSegmentationChart";
import { useCompanyRevenueProductSegmentation } from "./hooks/useCompanyRevenueProductSegmentation";
import ReturnMetricsChart from "./components/ReturnMetricsChart";
import HistoricPERatioChart from "./components/HistoricPERatioChart";
import HistoricPriceToFcfChart from "./components/HistoricPriceToFcfChart";
import FinancialPeriodSelector from "./components/shared/FinancialPeriodSelector";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  DEFAULT_ANNUAL_LIMIT,
  getApiPeriodParam,
} from "./components/shared/financialPeriodConfig";
import { DividendsTab } from "./components/DividendsTab";
import { NewsTab } from "./components/NewsTab";
import { InsiderTradesTab } from "./components/InsiderTradesTab";
import { InstitutionalOwnershipTab } from "./components/InstitutionalOwnershipTab";
import CompanyKeyStatistics from "./components/CompanyKeyStatistics";
import StockAnalysisReportDisplay from "./components/StockAnalysisReportDisplay";
import { ValuationTab } from "./components/ValuationTab";
import { CompanyHoldingsTab } from "./components/CompanyHoldingsTab";
import EarningsTranscriptTab, {
  useEarningsTranscriptTabState,
} from "./components/EarningsTranscriptTab";
import { isNative } from "../../../lib/platform";
import {
  BOTTOM_TAB_BAR_HEIGHT,
  BOTTOM_TAB_BAR_SAFE_AREA,
} from "../../Native/BottomTabBar";
import { MOBILE_TOPBAR_HEIGHT } from "../../../contexts/SidebarContext";
import { useAnalytics } from "../../../hooks/useAnalytics";
import TranscriptControls from "./components/TranscriptControls";
import { Theme } from "@mui/material/styles";
import { useStockCurrency } from "../../../contexts/StockCurrencyContext";
import { roundedPanelSx } from "../overviewPanel";

// Flat UI Color System - Theme Aware
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth (similar in both themes)
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Negative/Decline States (similar in both themes)
    negative: "#ef4444", // Clear red for losses/negative data
    negativeHover: "#dc2626", // Darker red for hover

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9", // Very subtle borders

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text

    // Accent/Information (slightly adjusted for dark)
    accent: isDark ? "#60a5fa" : "#3b82f6", // Brighter blue in dark mode
    warning: isDark ? "#fbbf24" : "#f59e0b", // Brighter orange in dark mode
  };
};

// Flat UI Typography System
const flatUITypography = {
  // Display - Company name, main price
  display: {
    fontSize: "3rem", // 48px
    fontWeight: 700, // Bold
    lineHeight: 1.1,
    letterSpacing: "-0.02em",
  },

  // Heading 1 - Tab titles, section headers
  h1: {
    fontSize: "2rem", // 32px
    fontWeight: 600, // Semi-bold
    lineHeight: 1.2,
  },

  // Heading 2 - Chart titles, metric labels
  h2: {
    fontSize: "1.25rem", // 20px
    fontWeight: 600,
    lineHeight: 1.3,
  },

  // Body - General content
  body: {
    fontSize: "1rem", // 16px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },

  // Caption - Small metrics, timestamps
  caption: {
    fontSize: "0.875rem", // 14px
    fontWeight: 400,
    lineHeight: 1.4,
  },

  // Label - Form labels, tiny text
  label: {
    fontSize: "0.75rem", // 12px
    fontWeight: 500, // Medium
    lineHeight: 1.3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
};

// Flat UI Chart Theme for Nivo - Theme Aware
export const getFlatChartTheme = (theme: Theme) => {
  const colors = getFlatUIColors(theme);

  return {
    background: "transparent",

    // Typography
    text: {
      fontSize: 12,
      fill: colors.textSecondary,
      fontFamily: "system-ui, -apple-system, sans-serif",
    },

    // Remove all shadows and depths
    axis: {
      domain: {
        line: {
          stroke: colors.border,
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: 12,
          fill: colors.textPrimary,
          fontWeight: 500,
        },
      },
      ticks: {
        line: {
          stroke: colors.border,
          strokeWidth: 1,
        },
        text: {
          fontSize: 11,
          fill: colors.textSecondary,
        },
      },
    },

    // Grid lines
    grid: {
      line: {
        stroke: colors.borderSubtle,
        strokeWidth: 1,
        strokeOpacity: 0.8,
      },
    },

    // Remove decorative tooltip styling
    tooltip: {
      container: {
        background: colors.surface, // Use surface color for better visibility
        color: colors.textPrimary,
        fontSize: 12,
        borderRadius: 0, // Sharp corners
        boxShadow: "none", // No shadow
        border: `1px solid ${colors.border}`,
      },
    },
  };
};

interface ViewTickerProps {
  company: Quote;
  ticker: string;
}

// Watchlist Toggle Button Component
const WatchlistToggleButton: React.FC<{ ticker: string }> = ({ ticker }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);

  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { isInWatchlist, memberships } = useIsStockInWatchlist(ticker);
  const { watchlists, selectedWatchlistId, selectWatchlist } = useSelectedWatchlist();

  // With a single watchlist there's nothing to choose, so the button stays a
  // one-tap toggle. Only once the user has several does picking make sense.
  const hasMultipleWatchlists = watchlists.length > 1;
  const singleMembership = memberships[0];
  const targetWatchlistId = singleMembership?.watchlistId ?? selectedWatchlistId;

  const addMutation = useAddStockToWatchlist(targetWatchlistId ?? "");
  const removeMutation = useRemoveStockFromWatchlist(targetWatchlistId ?? "");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleWatchlists) {
      setPickerAnchorEl(event.currentTarget);
      return;
    }

    if (!targetWatchlistId) return;

    const callbacks = {
      onSuccess: () => hapticSelection(),
      onError: () => hapticError(),
    };

    if (isInWatchlist && singleMembership) {
      removeMutation.mutate(
        { holdingId: singleMembership.holdingId, ticker },
        callbacks,
      );
      return;
    }
    addMutation.mutate({ ticker }, callbacks);
  };

  const isLoading = addMutation.isPending || removeMutation.isPending;

  return (
    <>
    <IconButton
      size="small"
      onClick={handleClick}
      aria-haspopup={hasMultipleWatchlists ? "menu" : undefined}
      aria-expanded={hasMultipleWatchlists ? !!pickerAnchorEl : undefined}
      disabled={(!targetWatchlistId && !hasMultipleWatchlists) || isLoading}
      sx={{
        color: !isInWatchlist
          ? flatUIColors.primary
          : flatUIColors.textSecondary,
        transition: "all 0.2s ease",
        marginLeft: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          backgroundColor: !isInWatchlist
            ? theme.palette.mode === "dark"
              ? "rgba(34, 197, 94, 0.1)"
              : "rgba(34, 197, 94, 0.05)"
            : flatUIColors.surface,
          color: !isInWatchlist
            ? flatUIColors.primaryHover
            : flatUIColors.textPrimary,
        },
        "&:disabled": {
          color: flatUIColors.textMuted,
          opacity: 0.5,
        },
      }}
      aria-label={isInWatchlist ? "Remove from watchlist" : "Add to watchlist"}
    >
      {isLoading ? (
        <CircularProgress size={24} sx={{ color: flatUIColors.textMuted }} />
      ) : !isInWatchlist ? (
        <VisibilityIcon sx={{ fontSize: 24 }} />
      ) : (
        <VisibilityOffIcon sx={{ fontSize: 24 }} />
      )}
    </IconButton>

    <WatchlistMembershipPicker
      open={!!pickerAnchorEl}
      anchorEl={pickerAnchorEl}
      onClose={() => setPickerAnchorEl(null)}
      ticker={ticker}
      watchlists={watchlists}
      memberships={memberships}
      onCreate={() => setIsCreateOpen(true)}
    />

    <WatchlistNameModal
      open={isCreateOpen}
      onClose={() => setIsCreateOpen(false)}
      onCreated={(created) => selectWatchlist(created.id)}
    />
    </>
  );
};

// Flat UI Compact Header Component
const CompactHeader: React.FC<{
  company: Quote;
  ticker: string;
  aftermarketTrade?: AftermarketTrade;
  isMarketOpen?: boolean;
}> = ({ company, ticker, aftermarketTrade, isMarketOpen }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme); // Get theme-aware colors
  // Match custom breakpoint used in ViewTicker main component
  const isMobile = useMediaQuery("(max-width: 1080px)");
  const { isMembershipActive } = useMembership();
  const { symbol, name, price, changePercentage, change, exchange } = company;
  const isPositive = (changePercentage ?? 0) >= 0;
  // Get currency-aware formatting from context
  const { formatPrice, formatChange } = useStockCurrency();
  const navigate = useNavigate();

  // MOBILE: Compact single-row layout (not sticky - MobileTopBar handles sticky navigation)
  if (isMobile) {
    return (
      <Box
        data-tour="stock-header"
        sx={{
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: `1px solid ${flatUIColors.border}`,
          backgroundColor: flatUIColors.background,
        }}
      >
        {/* Left: Logo + Symbol */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Logo
            src={getSymbolImageUrl(symbol)}
            alt={`${symbol} logo`}
            ticker={symbol}
            companyName={name}
            size={32}
          />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: flatUIColors.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {symbol}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: flatUIColors.textSecondary,
                lineHeight: 1.2,
              }}
            >
              {exchange}
            </Typography>
          </Box>
        </Box>

        {/* Centre: Close price + optional after-hours, side by side */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            justifyContent: "flex-end",
            mr: 1,
          }}
        >
          {/* Close price */}
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color: flatUIColors.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {formatPrice(price)}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: isPositive
                  ? flatUIColors.primary
                  : flatUIColors.negative,
                lineHeight: 1.2,
              }}
            >
              {isPositive ? "+" : ""}
              {formatPercentage(changePercentage ?? 0)}%
            </Typography>
          </Box>

          {/* After-hours price — shown inline when market is closed */}
          {aftermarketTrade &&
            isMarketOpen === false &&
            (() => {
              const afterHoursPrice = aftermarketTrade.price;
              const afterHoursChange = afterHoursPrice - price;
              const afterHoursChangePercentage =
                (afterHoursChange / price) * 100;
              const isAfterHoursPositive = afterHoursChange >= 0;
              return (
                <>
                  <Box
                    sx={{
                      width: "1px",
                      height: 32,
                      backgroundColor: flatUIColors.border,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ textAlign: "right" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                    >
                      <NightsStayIcon
                        sx={{ fontSize: 12, color: flatUIColors.textMuted }}
                      />
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: flatUIColors.textPrimary,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatPrice(afterHoursPrice)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: isAfterHoursPositive
                          ? flatUIColors.primary
                          : flatUIColors.negative,
                        lineHeight: 1.2,
                      }}
                    >
                      {isAfterHoursPositive ? "+" : ""}
                      {afterHoursChangePercentage.toFixed(2)}%
                    </Typography>
                  </Box>
                </>
              );
            })()}
        </Box>

        {/* Watchlist button */}
        <WatchlistToggleButton ticker={ticker} />
      </Box>
    );
  }

  // DESKTOP: Original horizontal layout
  return (
    <Box
      data-tour="stock-header"
      sx={{
        height: 64, // Fixed height to match Sidebar logo section
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        borderBottom: `1px solid ${flatUIColors.border}`,
        backgroundColor: flatUIColors.background,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left Section - Company Info and Price */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flex: "0 0 auto",
        }}
      >
        <Logo
          src={getSymbolImageUrl(symbol)}
          alt={`${symbol} logo`}
          ticker={symbol}
          companyName={name}
          size={40}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            sx={{
              ...flatUITypography.body,
              fontWeight: 600,
              color: flatUIColors.textPrimary,
              lineHeight: 1,
            }}
          >
            {name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                ...flatUITypography.caption,
                color: flatUIColors.textSecondary,
                fontWeight: 500,
              }}
            >
              {symbol}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                backgroundColor: flatUIColors.surface,
                border: `1px solid ${flatUIColors.border}`,
                borderRadius: 0, // Sharp corners
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  ...flatUITypography.label,
                  color: flatUIColors.textSecondary,
                  fontSize: "0.65rem",
                }}
              >
                {exchange}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Watch/Unwatch Icon - Full height container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <WatchlistToggleButton ticker={ticker} />
        </Box>

        {/* Divider */}
        <Box
          sx={{
            width: "1px",
            height: "40px",
            backgroundColor: flatUIColors.border,
            mx: 1,
          }}
        />

        {/* Price Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: flatUIColors.textPrimary,
            }}
          >
            {formatPrice(price)}
          </Typography>
          <Typography
            sx={{
              ...flatUITypography.caption,
              fontWeight: 600,
              color: isPositive ? flatUIColors.primary : flatUIColors.negative,
            }}
          >
            {formatChange(change)} ({isPositive ? "+" : ""}
            {formatPercentage(changePercentage ?? 0)}%)
          </Typography>
        </Box>

        {/* After Hours Trade - Only show if available and market is closed */}
        {aftermarketTrade &&
          isMarketOpen === false &&
          (() => {
            const afterHoursPrice = aftermarketTrade.price;
            const afterHoursChange = afterHoursPrice - price;
            const afterHoursChangePercentage = (afterHoursChange / price) * 100;
            const isAfterHoursPositive = afterHoursChange >= 0;

            return (
              <>
                {/* Divider */}
                <Box
                  sx={{
                    width: "1px",
                    height: "40px",
                    backgroundColor: flatUIColors.border,
                    mx: 1,
                  }}
                />

                {/* After Hours Price */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <NightsStayIcon
                    sx={{
                      fontSize: 18,
                      color: flatUIColors.textMuted,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: flatUIColors.textSecondary,
                    }}
                  >
                    {formatPrice(afterHoursPrice)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: isAfterHoursPositive
                        ? flatUIColors.primary
                        : flatUIColors.negative,
                    }}
                  >
                    {formatChange(afterHoursChange)} (
                    {isAfterHoursPositive ? "+" : ""}
                    {afterHoursChangePercentage.toFixed(2)}%)
                  </Typography>
                </Box>
              </>
            );
          })()}
      </Box>

      {/* Right Section - Search Bar and Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginLeft: "auto",
          // Guarantees a gap from the price block even when the header is tight
          // (wide-price tickers), so the actions never visually touch the price.
          pl: 2,
        }}
      >
        {/* Chat with Insight AI — deep-links into a ticker-seeded conversation.
            Premium-gated like the search bar: shown to everyone but disabled with
            an upgrade tooltip for non-members (the span lets the tooltip fire even
            while the button is disabled). */}
        {!isMobile && (
          <Tooltip
            title={isMembershipActive ? "" : "Available with Insight Pro"}
            arrow
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<InsightAiIcon />}
                disabled={!isMembershipActive}
                onClick={() => navigate("/chat", { state: { ticker, name } })}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  // Keep it a single compact line so it always matches the
                  // Compare button's height and never wraps/touches the price
                  // on wide-price tickers (e.g. BRK-A).
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  // Neutral by default — matches the Compare button; the Insight
                  // green only appears on hover (text, icon via currentColor, border).
                  borderColor: flatUIColors.border,
                  color: flatUIColors.textSecondary,
                  "&:hover": {
                    borderColor: "rgba(34, 197, 94, 0.6)",
                    color: "#22c55e",
                    backgroundColor: "rgba(34, 197, 94, 0.08)",
                  },
                }}
              >
                AI Chat
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Compare Button */}
        {!isMobile && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={() =>
              navigate("/compare", { state: { initialTicker: ticker } })
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontSize: "0.8125rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              flexShrink: 0,
              borderColor: flatUIColors.border,
              color: flatUIColors.textSecondary,
              "&:hover": {
                borderColor: flatUIColors.textSecondary,
                backgroundColor: flatUIColors.surface,
              },
            }}
          >
            Compare
          </Button>
        )}

        {/* Search Bar */}
        {!isMobile && (
          <Tooltip
            title={isMembershipActive ? "" : "Available with Insight Pro"}
            arrow
          >
            <Box
              sx={{
                width: "350px",
                "& .MuiAutocomplete-root": {
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "transparent",
                    borderRadius: 1.5,
                    height: "36px",
                    border: `1px solid ${flatUIColors.border}`,
                    transition: "all 0.2s ease",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(0, 0, 0, 0.01)",
                      borderColor: flatUIColors.textSecondary,
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                      border: `1px solid ${flatUIColors.primary}`,
                    },
                    "& input": {
                      fontSize: "0.875rem",
                      color: flatUIColors.textPrimary,
                      padding: "8px 12px",
                      "&::placeholder": {
                        color: flatUIColors.textMuted,
                        opacity: 1,
                      },
                    },
                    "& .MuiInputAdornment-root": {
                      color: flatUIColors.textMuted,
                    },
                  },
                },
              }}
            >
              <SearchBar
                placeholder="Search..."
                width="100%"
                variant="topbar"
                disabled={!isMembershipActive}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// Company Description Component with Show More/Less functionality
interface CompanyDescriptionProps {
  description: string;
}

const CompanyDescription: React.FC<CompanyDescriptionProps> = ({
  description,
}) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const [isExpanded, setIsExpanded] = useState(false);

  // Truncate description to approximately 450 characters at word boundary
  const truncatedDescription =
    description.length > 450
      ? description.substring(0, 450).split(" ").slice(0, -1).join(" ") + "..."
      : description;

  const shouldShowToggle = description.length > 450;

  return (
    <Box>
      <Typography
        sx={{
          ...flatUITypography.caption,
          color: flatUIColors.textPrimary,
          lineHeight: 1.6,
          maxWidth: "none",
        }}
      >
        {isExpanded ? description : truncatedDescription}
      </Typography>

      {shouldShowToggle && (
        <Button
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            mt: 1,
            p: 0,
            fontSize: "0.75rem",
            fontWeight: 500,
            color: flatUIColors.accent,
            textTransform: "none",
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "transparent",
              color: flatUIColors.primary,
            },
          }}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

// Tab indices map to these names for analytics (stock-only tabs are hidden for ETFs/funds).
const STOCK_TAB_NAMES = [
  "overview",
  "ai_analysis",
  "earnings",
  "financials",
  "valuation",
  "dividends",
  "insiders",
  "health",
  "news",
  "institutions",
  "holdings",
];

// `?tab=insiders` → its index, or -1. Mapped by NAME: a deep link written
// against an index would silently point at another tab the day one is added.
const tabIndexFromSearch = (search: string): number => {
  const name = new URLSearchParams(search).get("tab");
  return name ? STOCK_TAB_NAMES.indexOf(name) : -1;
};

// Main Component
const ViewTicker: React.FC<ViewTickerProps> = ({ company, ticker }) => {
  const theme = useTheme();
  const location = useLocation();
  const { trackFeature } = useAnalytics();
  const flatUIColors = useMemo(() => getFlatUIColors(theme), [theme]);
  const [tabValue, setTabValue] = useState(() =>
    Math.max(tabIndexFromSearch(location.search), 0),
  );
  const [financialsViewMode, setFinancialsViewMode] =
    useState<ViewMode>("quarterly");
  const [financialsLimit, setFinancialsLimit] = useState(
    DEFAULT_QUARTERLY_LIMIT,
  );
  // Transcript dates only load once the Earnings tab is opened
  const transcriptState = useEarningsTranscriptTabState(
    ticker,
    STOCK_TAB_NAMES[tabValue] === "earnings",
  );
  // Custom breakpoint at 1080px to include Surface Pro (912px) and iPad Pro landscape
  // but exclude laptops (typically 1280px+)
  const isMobile = useMediaQuery("(max-width: 1080px)");

  // Fetch data
  const { data: profileData, isLoading: isProfileLoading } =
    useCompanyProfile(ticker);

  // Market hours keyed off the quote's exchange — the quote is already
  // resolved before this component mounts, so this no longer waterfalls
  // behind the profile request.
  const { data: marketHours } = useExchangeMarketHours(company.exchange);

  // Aftermarket trade (extended-hours last price) — only fetched when the
  // market is closed; during trading hours the value is never displayed.
  const { data: aftermarketData } = useCompanyAftermarketTrade(
    ticker,
    marketHours?.isMarketOpen === false,
  );

  // Revenue segmentation only matters on the Financials tab — don't fetch
  // it from the Overview.
  const {
    data: revenueSegmentationData,
    isLoading: isRevenueSegmentationLoading,
  } = useCompanyRevenueProductSegmentation(
    STOCK_TAB_NAMES[tabValue] === "financials" ? ticker : undefined,
    getApiPeriodParam(financialsViewMode),
  );
  const hasRevenueSegmentation =
    !isRevenueSegmentationLoading &&
    !!revenueSegmentationData &&
    revenueSegmentationData.length > 0;

  // Extract profile for main content
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  // Funds and ETFs only show the Overview tab - other tabs aren't applicable
  const isEtfOrFund = profile?.isFund || profile?.isEtf;

  // `?tab=` deep links (a notification opens /charts?ticker=ASTS&tab=insiders).
  // Keyed on location.key, not the search string: opening a second
  // notification for the same ticker arrives at an identical URL and must
  // still re-apply the tab. One way only — tab clicks never write the URL, so
  // they add no history entries and native back is untouched.
  useEffect(() => {
    const index = tabIndexFromSearch(location.search);
    if (index >= 0 && !isEtfOrFund) setTabValue(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Funds and ETFs render the Overview tab alone, so a deep-linked stock tab
  // has nothing to select there once the profile says so.
  useEffect(() => {
    if (isEtfOrFund) setTabValue(0);
  }, [isEtfOrFund]);

  // Extract aftermarket trade (normalize to single object)
  const aftermarketTrade = Array.isArray(aftermarketData)
    ? aftermarketData[0]
    : aftermarketData;

  // Feature usage: which stock a user opened, and which detail tabs they use.
  useEffect(() => {
    trackFeature("stock_view", { ticker });
  }, [ticker, trackFeature]);

  // The tab strip scrolls horizontally with a hidden scrollbar, so two
  // affordances tell users more tabs exist: the tapped tab auto-centres
  // (scrollTo clamps at the ends, so edge tabs just pin flush), and edge
  // fades appear only while more tabs sit beyond that edge.
  const tabsWrapRef = useRef<HTMLDivElement | null>(null);
  const [tabsOverflow, setTabsOverflow] = useState({ left: false, right: false });

  useEffect(() => {
    const scroller = tabsWrapRef.current?.querySelector(".MuiTabs-scroller") as HTMLElement | null;
    if (!scroller) return;
    const update = () => {
      const left = scroller.scrollLeft > 4;
      const right = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4;
      setTabsOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    hapticSelection();
    setTabValue(newValue);
    const tab = event.currentTarget as HTMLElement | null;
    const scroller = tab?.closest(".MuiTabs-scroller") as HTMLElement | null;
    if (tab && scroller) {
      // Scroll ONLY the tab strip (scrollIntoView would also drag the
      // overflow-hidden page ancestors sideways).
      scroller.scrollTo({
        left: tab.offsetLeft - (scroller.clientWidth - tab.clientWidth) / 2,
        behavior: "smooth",
      });
    }
    trackFeature("stock_tab", {
      ticker,
      tab_name: STOCK_TAB_NAMES[newValue] ?? String(newValue),
    });
  };

  // Tab render functions with Flat UI Design
  const renderOverviewTab = () => {
    // MOBILE: Vertical stacked layout - Company Overview → Price Chart → Volume Chart
    if (isMobile) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {/* Compact Company Profile - Mobile */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 1.5,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {isProfileLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress
                  size={24}
                  sx={{ color: flatUIColors.primary }}
                />
              </Box>
            ) : profile ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  width: "100%",
                }}
              >
                {/* Sector */}
                {profile.sector && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        color: flatUIColors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Sector
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: flatUIColors.textPrimary,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {profile.sector}
                    </Typography>
                  </Box>
                )}
                {/* Industry */}
                {profile.industry && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        color: flatUIColors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Industry
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: flatUIColors.textPrimary,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {profile.industry}
                    </Typography>
                  </Box>
                )}
                {/* CEO */}
                {profile.ceo && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        color: flatUIColors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      CEO
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: flatUIColors.textPrimary,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {profile.ceo}
                    </Typography>
                  </Box>
                )}
                {/* Employees */}
                {profile.fullTimeEmployees && (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        color: flatUIColors.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Employees
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: flatUIColors.textPrimary,
                        fontWeight: 500,
                      }}
                    >
                      {Number(profile.fullTimeEmployees ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: flatUIColors.textMuted,
                  textAlign: "center",
                  py: 1,
                }}
              >
                Company profile unavailable
              </Typography>
            )}
          </Box>

          {/* Price Chart - Mobile optimized height */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 1.5,
              height: 280,
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <StockPriceChart
              ticker={ticker}
              company={company}
              height={250}
              renderCard={false}
              showFullscreen={true}
              showTimeRangeSelector={true}
            />
          </Box>

          {/* Volume Chart - Mobile optimized height */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 1.5,
              height: 300,
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <VolumeChart
              ticker={ticker}
              company={company}
              height="100%"
              renderCard={false}
              showFullscreen={true}
            />
          </Box>

          {/* Key Statistics - Compact for mobile */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 1.5,
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <CompanyKeyStatistics ticker={ticker} />
          </Box>

          {/* Recent Developments - Only show for stocks, not funds/ETFs */}
          {!isEtfOrFund && (
            <Box
              sx={{
                ...roundedPanelSx(theme.palette.mode === "dark"),
                p: 0,
                width: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <RecentDevelopmentsCard
                ticker={ticker}
                companyName={company.name || ""}
              />
            </Box>
          )}

          {/* Market Cap Chart */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 1.5,
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <MarketCapChart
              ticker={ticker}
              height={220}
              renderCard={false}
              showFullscreen={true}
            />
          </Box>
        </Box>
      );
    }

    // DESKTOP: Original side-by-side layout
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Company Info and Price Chart Row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: "row",
          }}
        >
          {/* Company Profile Information - Left Side */}
          {/* Auto-sizes based on content, constrained between 35-50% */}
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              flex: "0 0 auto",
              minWidth: "35%",
              maxWidth: "50%",
            }}
          >
            {isProfileLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress
                  size={32}
                  sx={{ color: flatUIColors.primary }}
                />
              </Box>
            ) : profile ? (
              <>
                {/* Company Profile Heading */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: flatUIColors.textPrimary,
                    mb: 4,
                  }}
                >
                  Company Profile
                </Typography>

                {/* Top Row: CEO, Sector, Industry */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {profile.ceo && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        CEO
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.textPrimary,
                        }}
                      >
                        {profile.ceo}
                      </Typography>
                    </Box>
                  )}

                  {profile.sector && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        SECTOR
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.textPrimary,
                        }}
                      >
                        {profile.sector}
                      </Typography>
                    </Box>
                  )}

                  {profile.industry && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        INDUSTRY
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.textPrimary,
                        }}
                      >
                        {profile.industry}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Bottom Row: Exchange, Full-time Employees, Website */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {profile.exchange && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        EXCHANGE
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.textPrimary,
                        }}
                      >
                        {profile.exchange}
                      </Typography>
                    </Box>
                  )}

                  {profile.fullTimeEmployees && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        EMPLOYEES
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.textPrimary,
                        }}
                      >
                        {Number(
                          profile.fullTimeEmployees ?? 0,
                        ).toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  {profile.website && (
                    <Box>
                      <Typography
                        sx={{
                          ...flatUITypography.label,
                          color: flatUIColors.textMuted,
                          mb: 0.5,
                        }}
                      >
                        WEBSITE
                      </Typography>
                      <Typography
                        sx={{
                          ...flatUITypography.caption,
                          color: flatUIColors.primary,
                          cursor: "pointer",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        component="a"
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {profile.website
                          .replace(/^https?:\/\//, "")
                          .replace(/^www\./, "")}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Company Description with Show More/Less */}
                {profile.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        ...flatUITypography.label,
                        color: flatUIColors.textMuted,
                        mb: 0.5,
                      }}
                    >
                      ABOUT THE COMPANY
                    </Typography>
                    <CompanyDescription description={profile.description} />
                  </Box>
                )}

                {/* Company Key Statistics */}
                <CompanyKeyStatistics ticker={ticker} />
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography
                  sx={{
                    ...flatUITypography.caption,
                    color: flatUIColors.textMuted,
                  }}
                >
                  Company profile data unavailable
                </Typography>
              </Box>
            )}
          </Box>

          {/* Charts Column - Intraday Price and Volume */}
          {/* Takes remaining space, stretches to match left column height */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Intraday Price Chart - Top */}
            <Box
              sx={{
                ...roundedPanelSx(theme.palette.mode === "dark"),
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                // Floor the height so the chart stays readable even when the
                // left column is short (e.g. company statistics fail to load).
                minHeight: 340,
              }}
            >
              <StockPriceChart
                ticker={ticker}
                company={company}
                height="100%"
                renderCard={false}
                showFullscreen={true}
                showTimeRangeSelector={true}
              />
            </Box>

            {/* Volume Chart - Bottom */}
            <Box
              sx={{
                ...roundedPanelSx(theme.palette.mode === "dark"),
                display: "flex",
                flexDirection: "column",
                flex: 1,
                // Floor the height so the chart stays readable even when the
                // left column is short (e.g. company statistics fail to load).
                minHeight: 320,
              }}
            >
              <VolumeChart
                ticker={ticker}
                company={company}
                height="100%"
                renderCard={false}
                showFullscreen={true}
              />
            </Box>
          </Box>
        </Box>

        {/* Recent Developments - Full Width (only for stocks, not funds/ETFs) */}
        {!isEtfOrFund && (
          <Box
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              p: 0,
            }}
          >
            <RecentDevelopmentsCard
              ticker={ticker}
              companyName={company.name || ""}
            />
          </Box>
        )}

        {/* Market Cap Chart - Full Width */}
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            p: 1.5,
          }}
        >
          <MarketCapChart
            ticker={ticker}
            height={300}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>
      </Box>
    );
  };

  const renderFinancialsTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Mobile: Period selector inside tab content */}
      {isMobile && (
        <FinancialPeriodSelector
          viewMode={financialsViewMode}
          onViewModeChange={setFinancialsViewMode}
          limit={financialsLimit}
          onLimitChange={setFinancialsLimit}
        />
      )}

      {/* Main Financial Metrics Grid - 2x3 layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <RevenueChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        {/* Revenue Projections - analyst revenue estimates, paired beside Revenue */}
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <RevenueProjectionsChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>
        {/* Historic PE Ratio Chart - half width (left) beside segmentation, else full width */}
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            ...(!hasRevenueSegmentation && { gridColumn: { md: "span 2" } }),
          }}
        >
          <HistoricPERatioChart
            ticker={ticker}
            height={320}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>
        {/* Revenue Segmentation - half width on the right, paired with the P/E chart when available */}
        {hasRevenueSegmentation && (
          <RevenueSegmentationChart
            ticker={ticker}
            height={320}
            renderCard={true}
            showFullscreen={false}
            viewMode={financialsViewMode}
          />
        )}
        {/* Historic Price / Free Cash Flow Chart - Spans full width */}
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            gridColumn: { md: "span 2" },
          }}
        >
          <HistoricPriceToFcfChart
            ticker={ticker}
            height={320}
            renderCard={false}
            showFullscreen={true}
          />
        </Box>
        <Box
          data-tour="eps-chart"
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <EPSChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <ReturnMetricsChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <CashFlowBreakdownChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <CashDebtBreakdownChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <EbitdaChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <NetIncomeChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <EmployeeCountChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            limit={financialsLimit}
          />
        </Box>
        <Box
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.03)",
            },
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <SharesOutstandingChart
            ticker={ticker}
            height={260}
            renderCard={false}
            showFullscreen={true}
            viewMode={financialsViewMode}
            limit={financialsLimit}
          />
        </Box>
      </Box>

      {/* Profit Margins Chart - Full Width */}
      <Box
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
        }}
      >
        <MarginsChart
          ticker={ticker}
          height={320}
          renderCard={false}
          showFullscreen={true}
          viewMode={financialsViewMode}
          limit={financialsLimit}
        />
      </Box>

      {/* Expense Breakdown Chart - Full Width for Better Legend Display */}
      <Box
        sx={{
          ...roundedPanelSx(theme.palette.mode === "dark"),
        }}
      >
        <ExpensesChart
          ticker={ticker}
          height={320}
          renderCard={false}
          showFullscreen={true}
          viewMode={financialsViewMode}
          limit={financialsLimit}
        />
      </Box>
    </Box>
  );

  const renderFinancialHealthTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
          <AltmanZScoreChart ticker={ticker} height={240} renderCard={false} />
        </Box>
        <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
          <PiotroskiScoreChart ticker={ticker} height={240} renderCard={false} />
        </Box>
      </Box>
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <CashDebtBreakdownChart
          ticker={ticker}
          height={280}
          renderCard={false}
          showFullscreen={true}
          viewMode="quarterly"
          limit={20}
        />
      </Box>
    </Box>
  );

  const renderNewsTab = () => <NewsTab ticker={ticker} />;

  const renderStockAnalysisTab = () => (
    <Box
      sx={{
        backgroundColor: flatUIColors.background,
        // Same native-chrome fix as the Earnings tab: bound the tab to the
        // visible viewport so ReportsView's internal scroller (height: 100%)
        // ends above the bottom tab bar instead of running behind it.
        // Chrome here = header(56) + tab-nav(64) + 16px gap — no controls row.
        ...(isMobile && {
          height: `calc(100dvh - 136px${isNative() ? ` - ${MOBILE_TOPBAR_HEIGHT + BOTTOM_TAB_BAR_HEIGHT}px - env(safe-area-inset-top, 0px) - ${BOTTOM_TAB_BAR_SAFE_AREA}` : ""})`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }),
      }}
    >
      <StockAnalysisReportDisplay
        ticker={ticker}
        companyName={company.name || ""}
      />
    </Box>
  );

  const renderInsiderTradesTab = () => <InsiderTradesTab ticker={ticker} />;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Mobile: natural page scroll; Desktop: fixed viewport with internal scroll.
        // The AI Analysis and Earnings tabs are viewport-bounded (they scroll
        // internally), so forcing the page to 100vh there just leaves a dead
        // scrollable region under the card.
        height: isMobile ? "auto" : "100vh",
        minHeight:
          isMobile && tabValue !== 1 && tabValue !== 2 ? "100vh" : undefined,
        backgroundColor: flatUIColors.background,
        // Prevent horizontal overflow
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Compact Header */}
      <CompactHeader
        company={company}
        ticker={ticker}
        aftermarketTrade={aftermarketTrade}
        isMarketOpen={marketHours?.isMarketOpen}
      />

      {/* Main Layout */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Main Content Area with Tabs */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: isMobile ? "visible" : "auto",
            backgroundColor: flatUIColors.background,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          {/* Flat Tab Navigation */}
          <Box
            sx={{
              px: { xs: 0, sm: 3 },
              paddingTop: 2,
              backgroundColor: flatUIColors.background,
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box ref={tabsWrapRef} sx={{ position: "relative", flex: 1, minWidth: 0 }}>
            <Tabs
              data-tour="stock-tabs"
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minWidth: 0,
                "& .MuiTabs-indicator": {
                  display: "none", // Hide the default indicator since we're using background colors
                },
                "& .MuiTabs-scroller": {
                  overflow: "auto !important",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
                "& .MuiTabs-flexContainer": {
                  gap: { xs: 0.5, sm: 1 }, // Smaller gap on mobile
                  px: { xs: 2, sm: 0 }, // Add padding inside on mobile
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  ...flatUITypography.body,
                  fontWeight: 500,
                  color: flatUIColors.textSecondary,
                  py: 0,
                  px: { xs: 1.5, sm: 2 },
                  minHeight: 32,
                  minWidth: "auto",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                  border: `1px solid ${flatUIColors.border}`,
                  borderRadius: 0.5, // Very subtle rounding
                  transition: "all 200ms linear",
                  whiteSpace: "nowrap",
                  "&.Mui-selected": {
                    color: flatUIColors.primary,
                    fontWeight: 600,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                    borderColor: flatUIColors.primary,
                  },
                  "&:hover": {
                    color: flatUIColors.textPrimary,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#212121" : "#e5e5e5",
                  },
                },
              }}
            >
              <Tab data-tour="tab-overview" label="Overview" />
              {/* Stock-only tabs - hidden for funds and ETFs */}
              {!isEtfOrFund && [
                <Tab
                  key="ai"
                  data-tour="tab-ai-analysis"
                  label="AI Analysis"
                />,
                <Tab
                  key="earnings"
                  data-tour="tab-earnings"
                  label="Earnings"
                />,
                <Tab key="fin" data-tour="tab-financials" label="Financials" />,
                <Tab key="val" label="Valuation" />,
                <Tab
                  key="dividends"
                  data-tour="tab-dividends"
                  label="Dividends"
                />,
                <Tab
                  key="insiders"
                  data-tour="tab-insider-trades"
                  label="Insiders"
                />,
                <Tab key="health" label="Health" />,
                <Tab key="news" data-tour="tab-news" label="News" />,
                <Tab
                  key="inst"
                  data-tour="tab-institutional-ownership"
                  label="Institutions"
                />,
                <Tab
                  key="holdings"
                  data-tour="tab-company-holdings"
                  label="Holdings"
                />,
              ]}
            </Tabs>
            {/* Scroll-affordance fades: visible only while more tabs sit
                beyond that edge, so a strip that exactly fits shows nothing */}
            {tabsOverflow.left && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 28,
                  pointerEvents: "none",
                  background: `linear-gradient(to right, ${flatUIColors.background}, transparent)`,
                }}
              />
            )}
            {tabsOverflow.right && (
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 28,
                  pointerEvents: "none",
                  background: `linear-gradient(to left, ${flatUIColors.background}, transparent)`,
                }}
              />
            )}
            </Box>

            {/* Desktop: Period selector alongside tabs when Financials is active */}
            {!isMobile && !isEtfOrFund && tabValue === 3 && (
              <Box sx={{ pr: 2 }}>
                <FinancialPeriodSelector
                  viewMode={financialsViewMode}
                  onViewModeChange={setFinancialsViewMode}
                  limit={financialsLimit}
                  onLimitChange={setFinancialsLimit}
                />
              </Box>
            )}
          </Box>

          {/* Transcript controls — rendered below the tab row when Earnings tab is active */}
          {!isEtfOrFund && tabValue === 2 && (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1.5,
                backgroundColor: flatUIColors.background,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <TranscriptControls
                quarterOptions={transcriptState.quarterOptions}
                selectedQuarter={transcriptState.selectedQuarter}
                onQuarterSelect={transcriptState.onQuarterSelect}
                mode={transcriptState.mode}
                onModeChange={(mode) => transcriptState.onStateChange({ mode })}
                isLoading={transcriptState.isLoadingDates}
              />
            </Box>
          )}

          {/* Tab Content with Flat Background */}
          <Box
            sx={{
              flex: 1,
              overflowX: "hidden",
              overflowY: isMobile ? "visible" : "auto",
              px: !isEtfOrFund && tabValue === 2 ? 0 : { xs: 2, sm: 3 },
              pb: !isEtfOrFund && tabValue === 2 ? 0 : 3,
              backgroundColor: flatUIColors.background,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            {tabValue === 0 && renderOverviewTab()}
            {/* Stock-only tab content - hidden for funds and ETFs */}
            {!isEtfOrFund && tabValue === 1 && renderStockAnalysisTab()}
            {!isEtfOrFund && tabValue === 2 && (
              // Height = 100vh minus all fixed chrome: header(64) + tab-nav(64) + controls(56) + bottom-gap(16)
              // Mobile: header is 56px instead of 64px
              // Native additionally subtracts the shell's FIXED chrome — the
              // top bar (56px + notch inset) and the bottom tab bar (+ home
              // indicator) — same fix as the /earnings page: a raw viewport
              // calc can't see any of it, so the transcript card's last lines
              // scrolled behind the tab bar. The flex column is what lets the
              // card's own flex-fill mode (fillHeight={false}) actually bound
              // it to this height and scroll internally.
              <Box
                sx={{
                  height: isMobile
                    ? `calc(100dvh - 192px${isNative() ? ` - ${MOBILE_TOPBAR_HEIGHT + BOTTOM_TAB_BAR_HEIGHT}px - env(safe-area-inset-top, 0px) - ${BOTTOM_TAB_BAR_SAFE_AREA}` : ""})`
                    : "calc(100vh - 200px)",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <EarningsTranscriptTab
                  ticker={ticker}
                  year={transcriptState.year}
                  quarter={transcriptState.quarter}
                  mode={transcriptState.mode}
                  quarterOptions={transcriptState.quarterOptions}
                  selectedQuarter={transcriptState.selectedQuarter}
                  isLoadingDates={transcriptState.isLoadingDates}
                  onStateChange={transcriptState.onStateChange}
                  onQuarterSelect={transcriptState.onQuarterSelect}
                />
              </Box>
            )}
            {!isEtfOrFund && tabValue === 3 && renderFinancialsTab()}
            {!isEtfOrFund && tabValue === 4 && <ValuationTab ticker={ticker} />}
            {!isEtfOrFund && tabValue === 5 && (
              <DividendsTab ticker={ticker} price={company.price} />
            )}
            {!isEtfOrFund && tabValue === 6 && renderInsiderTradesTab()}
            {!isEtfOrFund && tabValue === 7 && renderFinancialHealthTab()}
            {!isEtfOrFund && tabValue === 8 && renderNewsTab()}
            {!isEtfOrFund && tabValue === 9 && (
              <InstitutionalOwnershipTab ticker={ticker} />
            )}
            {!isEtfOrFund && tabValue === 10 && (
              <CompanyHoldingsTab
                cik={profile?.cik}
                companyName={company.name ?? ""}
                ticker={ticker}
                isProfileLoading={isProfileLoading}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ViewTicker;
